import React from 'react';
import { EducationFormatter } from '@/utils/educationFormatter';

interface ResumeData {
  personalInfo: {
    name: string;
    title: string;
    email: string;
    phone: string;
    location: string;
    linkedin?: string;
    website?: string;
  };
  summary: string;
  experience: Array<{
    title: string;
    company: string;
    location: string;
    dates: string;
    bullets: string[];
  }>;
  education: Array<{
    degree: string;
    school: string;
    location: string;
    dates: string;
    gpa?: string;
  }>;
  skills: string[];
}

interface Template7Props {
  data: ResumeData;
}

export const Template7: React.FC<Template7Props> = ({ data }) => {
  return (
    <div className="max-w-4xl mx-auto bg-white text-gray-900 font-sans">
      {/* Modern Header */}
      <header className="relative overflow-hidden">
        <div className="bg-gradient-to-r from-rose-500 to-pink-500 p-8">
          <div className="relative z-10">
            <h1 className="text-4xl font-bold text-white mb-2">{data.personalInfo.name}</h1>
            <h2 className="text-xl text-rose-100 mb-6">{data.personalInfo.title}</h2>
            
            <div className="grid grid-cols-2 gap-4 text-sm text-rose-100">
              <div>
                <p className="mb-1">{data.personalInfo.email}</p>
                <p>{data.personalInfo.phone}</p>
              </div>
              <div>
                <p className="mb-1">{data.personalInfo.location}</p>
                {data.personalInfo.linkedin && <p>{data.personalInfo.linkedin}</p>}
                {data.personalInfo.website && <p>{data.personalInfo.website}</p>}
              </div>
            </div>
          </div>
          
          {/* Geometric decoration */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-white bg-opacity-10 rounded-full -mr-16 -mt-16"></div>
          <div className="absolute bottom-0 right-8 w-20 h-20 bg-white bg-opacity-10 rounded-full"></div>
        </div>
      </header>

      <div className="p-8">
        {/* Summary */}
        <section className="mb-8">
          <div className="flex items-center mb-4">
            <div className="w-8 h-8 bg-rose-500 rounded-lg flex items-center justify-center mr-3">
              <span className="text-white text-sm font-bold">★</span>
            </div>
            <h3 className="text-xl font-bold text-gray-900">About Me</h3>
          </div>
          <div className="bg-rose-50 p-6 rounded-xl border-l-4 border-rose-500">
            <p className="text-gray-700 leading-relaxed">{data.summary}</p>
          </div>
        </section>

        {/* Experience */}
        <section className="mb-8">
          <div className="flex items-center mb-6">
            <div className="w-8 h-8 bg-rose-500 rounded-lg flex items-center justify-center mr-3">
              <span className="text-white text-sm font-bold">⚡</span>
            </div>
            <h3 className="text-xl font-bold text-gray-900">Work Experience</h3>
          </div>
          
          <div className="space-y-8">
            {data.experience.map((job, index) => (
              <div key={index} className="relative">
                {/* Timeline line */}
                {index < data.experience.length - 1 && (
                  <div className="absolute left-4 top-12 w-0.5 h-full bg-rose-200"></div>
                )}
                
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-rose-500 rounded-full flex items-center justify-center mr-6 relative z-10">
                    <span className="text-white text-xs font-bold">{index + 1}</span>
                  </div>
                  
                  <div className="flex-1">
                    <div className="bg-white border border-rose-200 rounded-xl p-6 shadow-sm">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h4 className="text-lg font-bold text-gray-900">{job.title}</h4>
                          <p className="text-rose-600 font-semibold">{job.company}</p>
                          <p className="text-sm text-gray-600">{job.location}</p>
                        </div>
                        <span className="bg-rose-100 text-rose-700 px-3 py-1 rounded-full text-sm font-medium">
                          {job.dates}
                        </span>
                      </div>
                      
                      <ul className="space-y-2">
                        {job.bullets.map((bullet, bulletIndex) => (
                          <li key={bulletIndex} className="flex items-start">
                            <span className="text-rose-500 mr-3 mt-1">▸</span>
                            <span className="text-gray-700">{bullet}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Education & Skills Grid */}
        <div className="grid grid-cols-2 gap-8">
          {/* Education */}
          <section>
            <div className="flex items-center mb-4">
              <div className="w-8 h-8 bg-rose-500 rounded-lg flex items-center justify-center mr-3">
                <span className="text-white text-sm font-bold">🎓</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900">Education</h3>
            </div>
            
            <EducationFormatter 
              education={data.education} 
              className="text-gray-900"
            />
          </section>

          {/* Skills */}
          <section>
            <div className="flex items-center mb-4">
              <div className="w-8 h-8 bg-rose-500 rounded-lg flex items-center justify-center mr-3">
                <span className="text-white text-sm font-bold">🔧</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900">Skills</h3>
            </div>
            
            <div className="grid grid-cols-1 gap-2">
              {data.skills.map((skill, index) => (
                <div key={index} className="bg-rose-100 text-rose-800 px-4 py-2 rounded-full text-sm font-medium text-center">
                  {skill}
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};